/**
 * Role Helper Utilities
 * 
 * Utility functions for working with user roles and permissions.
 */

import { USER_ROLES, hasRolePermission, getRoleDisplayName, isValidRole } from '../constants/roles';

/**
 * Get user role from localStorage or token
 * @returns {string|null} - User role or null if not found
 */
export const getUserRole = () => {
  // Try to get from localStorage
  const storedRole = localStorage.getItem('user_role');
  if (storedRole && isValidRole(storedRole)) {
    return storedRole;
  }

  // Try to get from user data in localStorage
  const userData = localStorage.getItem('user_data');
  if (userData) {
    try {
      const user = JSON.parse(userData);
      if (user.role && isValidRole(user.role)) {
        return user.role;
      }
    } catch (e) {
      console.error('Error parsing user data:', e);
    }
  }

  return null;
};

/**
 * Set user role in localStorage
 * @param {string} role - The role to set
 */
export const setUserRole = (role) => {
  if (isValidRole(role)) {
    localStorage.setItem('user_role', role);
  } else {
    console.warn(`Invalid role: ${role}`);
  }
};

/**
 * Check if current user is admin
 * @returns {boolean}
 */
export const isAdmin = () => {
  return getUserRole() === USER_ROLES.ADMIN;
};

/**
 * Check if current user is blogger
 * @returns {boolean}
 */
export const isBlogger = () => {
  return getUserRole() === USER_ROLES.BLOGGER;
};

/**
 * Check if current user is center admin
 * @returns {boolean}
 */
export const isCenterAdmin = () => {
  return getUserRole() === USER_ROLES.CENTER_ADMIN;
};

/**
 * Check if user has admin or center admin role
 * @returns {boolean}
 */
export const isAdminOrCenterAdmin = () => {
  const role = getUserRole();
  return role === USER_ROLES.ADMIN || role === USER_ROLES.CENTER_ADMIN;
};

/**
 * Check if user can access admin features
 * @returns {boolean}
 */
export const canAccessAdmin = () => {
  return hasRolePermission(getUserRole(), USER_ROLES.ADMIN);
};

/**
 * Check if user can manage content (blogger or higher)
 * @returns {boolean}
 */
export const canManageContent = () => {
  return hasRolePermission(getUserRole(), USER_ROLES.BLOGGER);
};

/**
 * Get current user's role display name
 * @returns {string}
 */
export const getCurrentUserRoleDisplay = () => {
  const role = getUserRole();
  return role ? getRoleDisplayName(role) : 'Guest';
};

/**
 * Clear user role from storage
 */
export const clearUserRole = () => {
  localStorage.removeItem('user_role');
  localStorage.removeItem('user_data');
};

const roleHelpers = {
  getUserRole,
  setUserRole,
  isAdmin,
  isBlogger,
  isCenterAdmin,
  isAdminOrCenterAdmin,
  canAccessAdmin,
  canManageContent,
  getCurrentUserRoleDisplay,
  clearUserRole,
  hasRolePermission,
  getRoleDisplayName,
  isValidRole,
};
export default roleHelpers;

